import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import SeoHead from "@/components/feature/SeoHead";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { principles, siteInfo, stats, testimonials } from "@/mocks/site";
import SubmitForm from "./components/SubmitForm";

const AboutPage = () => {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于塌房档案局",
      description:
        "了解塌房档案局的站点定位、整理理念、内容原则与投稿方式。",
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: siteInfo.name,
      url: "https://hnhxedu.cn/",
      description: siteInfo.intro,
      email: siteInfo.email,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://hnhxedu.cn/" },
        { "@type": "ListItem", position: 2, name: "关于与爆料", item: "https://hnhxedu.cn/about" },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <SeoHead
        title="关于与爆料 - 塌房档案局的定位、理念与投稿方式"
        description="了解塌房档案局的站点定位、发展理念、内容原则与联系方式，并通过投稿入口把你知道的网红塌房旧料线索交给编辑团队核对整理。"
        keywords="塌房档案局,网红塌房,关于我们,爆料投稿,内容原则"
        path="/about"
        jsonLd={jsonLd}
      />
      <SiteHeader />

      <main className="pt-16 md:pt-20">
        <section className="w-full px-4 md:px-8 py-10 md:py-16 bg-background-100 border-b border-background-200/80">
          <div className="max-w-7xl mx-auto">
            <Breadcrumbs items={[{ label: "首页", href: "/" }, { label: "关于与爆料" }]} />
            <h1 className="mt-5 font-heading font-black text-3xl md:text-5xl text-foreground-950 max-w-3xl">
              我们为什么做这个档案局
            </h1>
            <p className="mt-4 max-w-3xl text-sm md:text-base text-foreground-600 leading-relaxed">
              娱乐圈的瓜每天都在更新，但真正让人上头的，从来不是某一条截图，而是「这件事到底是怎么一步步走到今天」。
              塌房档案局想做的，就是把这根线捋清楚。
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-12 md:py-16">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div className="aspect-[4/3] rounded-lg overflow-hidden border border-background-200/80">
              <img
                src="https://readdy.ai/api/search-image?query=Cozy%20editorial%20illustration%20of%20an%20organized%20archive%20workspace%2C%20wooden%20desk%20with%20stacked%20folders%2C%20a%20timeline%20board%20with%20pinned%20notes%2C%20warm%20cream%20and%20terracotta%20tones%2C%20soft%20natural%20light%2C%20minimal%20stylized%20shapes%2C%20no%20text%20or%20letters&width=1200&height=900&seq=gua-about-story&orientation=landscape"
                alt="档案整理工作台插画，象征塌房档案局的整理流程"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <p className="text-sm font-medium text-primary-600 mb-2">网站定位</p>
              <h2 className="font-heading font-black text-2xl md:text-3xl text-foreground-950">
                一个只做整理的吃瓜档案库
              </h2>
              <p className="mt-4 text-sm md:text-base text-foreground-700 leading-loose">
                {siteInfo.intro}
              </p>
              <p className="mt-4 text-sm md:text-base text-foreground-700 leading-loose">
                我们不追热度、不写小作文、不给任何人贴标签。把信息按时间排好，把来源标清楚，
                剩下的判断交给每一个读到它的人。
              </p>

              <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
                {stats.map((item) => (
                  <div key={item.label}>
                    <p className="font-heading font-black text-2xl md:text-3xl text-primary-600">
                      {item.value}
                      <span className="text-sm font-bold ml-0.5">{item.suffix}</span>
                    </p>
                    <p className="mt-1 text-xs text-foreground-500">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-2xl mb-8 md:mb-12">
              <p className="text-sm font-medium text-primary-600 mb-2">内容原则</p>
              <h2 className="font-heading font-black text-2xl md:text-4xl text-foreground-950">
                我们的三条底线
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {principles.map((item) => (
                <article
                  key={item.title}
                  className="p-5 md:p-7 rounded-lg bg-background-50 border border-background-200/80"
                >
                  <span className="w-12 h-12 flex items-center justify-center rounded-md bg-accent-100 text-accent-700 text-2xl mb-4">
                    <i className={item.icon} />
                  </span>
                  <h3 className="font-heading font-bold text-lg text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
            <div className="lg:col-span-5">
              <p className="text-sm font-medium text-primary-600 mb-2">用户口碑</p>
              <h2 className="font-heading font-black text-2xl md:text-3xl text-foreground-950">
                吃瓜群众怎么说
              </h2>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                我们很少做推广，大部分用户都是被朋友「安利」过来的。下面是他们留下的几句真实感受。
              </p>

              <div className="mt-8 space-y-4">
                {testimonials.map((item) => (
                  <figure
                    key={item.name}
                    className="p-4 md:p-5 rounded-lg bg-background-100 border border-background-200/80"
                  >
                    <blockquote className="text-sm text-foreground-800 leading-relaxed">
                      {item.text}
                    </blockquote>
                    <figcaption className="mt-3 flex items-center gap-3">
                      <img
                        src={item.avatar}
                        alt={`${item.name} 头像`}
                        className="w-9 h-9 rounded-full object-cover border border-background-200"
                      />
                      <span className="text-xs text-foreground-500">
                        <span className="font-medium text-foreground-800">{item.name}</span> · {item.role}
                      </span>
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>

            <div className="lg:col-span-7 flex flex-col gap-6">
              <SubmitForm />

              <div className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-200/80">
                <h3 className="font-heading font-bold text-lg text-foreground-950 flex items-center gap-2">
                  <i className="ri-contacts-line text-primary-500" />
                  其他联系方式
                </h3>
                <ul className="mt-4 space-y-3 text-sm text-foreground-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-mail-line mt-0.5 text-primary-500" />
                    <span>
                      内容异议与商务合作：<span className="font-medium">{siteInfo.email}</span>
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-time-line mt-0.5 text-primary-500" />
                    <span>线索核对时间：工作日 10:00 - 18:00</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-shield-check-line mt-0.5 text-primary-500" />
                    <span>如认为站内内容与事实不符，请附上依据，我们会核对后修订或下架。</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
};

export default AboutPage;